import {
  createAction,
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
  nanoid,
} from "@reduxjs/toolkit";
import { format } from "date-fns";

import { RootState } from "../../app/store";
import { EventPayload } from "../../common/types/Event";
import {
  CustomerContract,
  CustomerOrganizationWithCostCenters,
  getCustomerOrgContracts,
} from "../customer/customerAPI";
import { INVOICE_DRAFT, INVOICE_EDITED, LOCAL_STORAGE_KEY } from "./constants";
import {
  AttachmentRequest,
  AttachmentUploadRequest,
  AttachmentUploadUrlRequest,
  AttachmentUrl,
  ConvertToCreditInvoice,
  convertToCreditInvoiceCommand,
  createNewManualInvoice,
  customerOrgById,
  CustomerOrgFilter,
  deleteInvoiceCmd,
  deleteInvoicesBundleCmd,
  EezyCompany,
  EezyCompanyFilters,
  getAttachmentDownloadUrl,
  getAttachmentUploadUrl,
  getEezyCompanies,
  getInvoiceById,
  getInvoicesByNumbers,
  getPaymentTerms,
  InvoiceAttachment,
  InvoiceEdit,
  InvoiceEvent,
  InvoiceRow,
  InvoiceSavePost,
  InvoicesEditFetch,
  LocalStorageAttachment,
  ManualInvoicePost,
  PaymentTerms,
  refreshInvoice,
  removeAttachment,
  saveInvoice,
  saveLocalDraft,
  uploadAttachment,
} from "./invoiceAPI";
import { changeInvoiceWave, sendInvoices } from "./invoiceSlice";

export type SliceState = {
  ids: [];
  entities: {};
  status: "idle" | "pending" | "succeeded" | "failed";
  error?: string;
};

// EntityAdapter needs id to be required but new Invoice does not have one so we need to make something up
const invoiceDetailedAdapter = createEntityAdapter<InvoiceEdit>({
  sortComparer: (a, b) =>
    (a.invoiceDate || "").localeCompare(b.invoiceDate || ""),
});

export const fetchEezyCompanies = createAsyncThunk<
  EezyCompany[],
  EezyCompanyFilters,
  { state: RootState }
>("invoicesDetailed/fetchEezyCompanies", async (filters: {}, { requestId }) => {
  const response = await getEezyCompanies(filters, {
    requestId,
  });
  return response;
});

export const fetchCustomerOrgById = createAsyncThunk<
  CustomerOrganizationWithCostCenters,
  CustomerOrgFilter,
  { state: RootState }
>("invoicesDetailed/fetchCustomerOrgById", async (filters, { requestId }) => {
  const response = await customerOrgById(filters, {
    requestId,
  });
  return response.data;
});

export const saveLocalInvoiceDraft = createAsyncThunk(
  "invoicesDetailed/saveLocalInvoiceDraft",
  async (invoice: InvoiceEdit, { requestId }) => {
    const response = await saveLocalDraft(invoice, { requestId });
    return response;
  },
);

export const fetchPaymentTerms = createAsyncThunk<
  PaymentTerms,
  EezyCompanyFilters,
  { state: RootState }
>(
  "invoicesDetailed/fetchPaymentTerms",
  async (filters: EezyCompanyFilters, { requestId }) => {
    const response = await getPaymentTerms(filters, { requestId });
    return response;
  },
);

export const createManualInvoice = createAsyncThunk<
  InvoiceEvent[],
  ManualInvoicePost,
  { state: RootState }
>(
  "invoicesDetailed/createManualInvoice",
  async (invoice: ManualInvoicePost, { requestId }) => {
    const response = await createNewManualInvoice(invoice, { requestId });
    return response.data;
  },
);

export const updateInvoice = createAsyncThunk<
  InvoiceEvent[],
  InvoiceSavePost,
  { state: RootState }
>(
  "invoicesDetailed/updateInvoice",
  async (invoice: InvoiceSavePost, { requestId }) => {
    const response = await saveInvoice(invoice, { requestId });
    return response.data;
  },
);

export const copyInvoice = createAsyncThunk<
  InvoiceEdit,
  InvoiceEdit,
  { state: RootState }
>("invoicesDetailed/copyInvoice", async (invoice: InvoiceEdit) => {
  // Remove properties that shouldn't be copied
  const {
    invoiceNumber: __,
    referenceNumber: ___,
    attachments: ____,
    invoiceWave: _____,
    ...invoiceData
  } = invoice;
  const newInvoice: InvoiceEdit = {
    ...invoiceData,
    id: "",
    invoiceDate: format(new Date(), "yyyy-MM-dd"),
    invoiceValueDate: "",
    invoiceStatus: INVOICE_DRAFT,
    invoiceRows: invoiceData.invoiceRows.map((row) => ({
      ...row,
      id: nanoid(),
      editable: true,
    })),
  };
  return newInvoice;
});

export const fetchInvoiceById = createAsyncThunk<
  InvoiceEdit,
  string,
  { state: RootState }
>(
  "invoicesDetailed/fetchInvoiceById",
  async (invoiceId: string, { requestId }) => {
    const response = await getInvoiceById(invoiceId, {
      requestId,
    });
    return response.data;
  },
);

export const fetchInvoicesByNumbers = createAsyncThunk<
  InvoicesEditFetch,
  string[],
  { state: RootState }
>(
  "invoicesDetailed/fetchInvoicesByNumber",
  async (numbers: string[], { requestId }) => {
    const response = await getInvoicesByNumbers(numbers, {
      requestId,
    });
    return response.data;
  },
);

export const refreshInvoiceAttachments = createAsyncThunk<
  InvoicesEditFetch,
  string[],
  { state: RootState }
>(
  "invoicesDetailed/refreshInvoiceAttachments",
  async (numbers: string[], { requestId }) => {
    const response = await getInvoicesByNumbers(numbers, {
      requestId,
    });
    return response.data;
  },
);

export const fetchCustomerOrgContracts = createAsyncThunk<
  CustomerContract[],
  string,
  { state: RootState }
>(
  "invoicesDetailed/fetchCustomerOrgContracts",
  async (customerOrgId: string, { requestId }) => {
    const response = await getCustomerOrgContracts(customerOrgId, {
      requestId,
    });
    return response.data;
  },
);

export const fetchAttachmentUploadUrl = createAsyncThunk<
  string,
  AttachmentUploadUrlRequest,
  { state: RootState }
>(
  "invoicesDetailed/fetchAttachmentUploadUrl",
  async (attachment: AttachmentUploadUrlRequest, { requestId }) => {
    const urlRequest = await getAttachmentUploadUrl(attachment, { requestId });
    return urlRequest.data.url;
  },
);

export const uploadInvoiceAttachment = createAsyncThunk<
  string,
  AttachmentUploadRequest,
  { state: RootState }
>(
  "invoicesDetailed/uploadInvoiceAttachment",
  async (attachment: AttachmentUploadRequest, { requestId }) => {
    await uploadAttachment(attachment.url, attachment.content);
    const filename = decodeURIComponent(
      attachment.url
        .substring(0, attachment.url.lastIndexOf("?"))
        .substring(attachment.url.lastIndexOf("/") + 1),
    );
    return filename;
  },
);

export const fetchAttachmentDownloadUrl = createAsyncThunk<
  AttachmentUrl,
  AttachmentRequest,
  { state: RootState }
>(
  "invoicesDetailed/fetchAttachmentDownloadUrl",
  async (attachment: AttachmentRequest, { requestId }) => {
    const response = await getAttachmentDownloadUrl(attachment, { requestId });
    return response.data;
  },
);

export const deleteAttachment = createAsyncThunk<number, AttachmentRequest>(
  "invoicesDetailed/deleteAttachment",
  async (attachment: AttachmentRequest, { requestId }) => {
    const response = await removeAttachment(attachment, { requestId });
    return response.status;
  },
);

export const deleteInvoice = createAsyncThunk<
  InvoiceEvent[],
  string,
  { state: RootState }
>(
  "invoicesDetailed/deleteInvoice",
  async (invoiceId: string, { requestId }) => {
    const response = await deleteInvoiceCmd(invoiceId, { requestId });
    return response.data;
  },
);

export const deleteInvoicesBundle = createAsyncThunk<
  InvoiceEvent[],
  string,
  { state: RootState }
>(
  "invoicesDetailed/deleteInvoicesBundle",
  async (bundleId: string, { requestId }) => {
    const response = await deleteInvoicesBundleCmd(bundleId, { requestId });
    return response.data;
  },
);

export const refreshInvoiceData = createAsyncThunk<
  InvoiceEvent[],
  string,
  { state: RootState }
>("invoicesDetailed/refreshInvoiceData", async (invoiceId: string) => {
  const response = await refreshInvoice(invoiceId);
  return response.data;
});

export const updateAttachmentsInLocalStorage = createAction(
  "invoicesDetailed/updateAttachmentsInLocalStorage",
  (invoiceId: string) => {
    const item = localStorage.getItem(LOCAL_STORAGE_KEY);
    const localStorageData: LocalStorageAttachment[] = !!item
      ? JSON.parse(item)
      : [];

    return {
      payload: { localStorageData, invoiceId },
    };
  },
);

export const getAttachmentsFromLocalStorage = createAction(
  "invoicesDetailed/updateAttachmentsFromLocalStorage",
  (invoiceId: string) => {
    const item = localStorage.getItem(LOCAL_STORAGE_KEY);
    const localStorageData: LocalStorageAttachment[] = !!item
      ? JSON.parse(item)
      : [];

    const attachments =
      localStorageData &&
      localStorageData.filter(
        (attachment) =>
          attachment.path === "invoices" && attachment.pathId === invoiceId,
      );
    return {
      payload: { attachments: [...attachments], invoiceId },
    };
  },
);
export const convertToCreditInvoice = createAsyncThunk<
  EventPayload<InvoiceEdit>[],
  ConvertToCreditInvoice,
  { state: RootState }
>(
  "invoicesDetailed/convertToCreditInvoice",
  async (body: ConvertToCreditInvoice) => {
    const response = await convertToCreditInvoiceCommand(body);
    return response.data;
  },
);

export const invoiceDetailedSlice = createSlice({
  name: "invoices",
  initialState: invoiceDetailedAdapter.getInitialState({
    status: "idle",
  }) as SliceState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(saveLocalInvoiceDraft.pending, (state) => {
        state.status = "pending";
      })
      .addCase(saveLocalInvoiceDraft.fulfilled, (state, action) => {
        invoiceDetailedAdapter.setOne(state, action.payload);
        state.status = "succeeded";
      })
      .addCase(saveLocalInvoiceDraft.rejected, (state, action) => {
        state.error = action.error.message;
        state.status = "failed";
      })
      .addCase(fetchInvoicesByNumbers.pending, (state) => {
        state.status = "pending";
      })
      .addCase(fetchInvoicesByNumbers.fulfilled, (state, action) => {
        invoiceDetailedAdapter.setAll(state, []);
        invoiceDetailedAdapter.setAll(state, action.payload.data);
        state.status = "succeeded";
      })
      .addCase(fetchInvoicesByNumbers.rejected, (state, action) => {
        state.error = action.error.message;
        state.status = "failed";
      })
      .addCase(refreshInvoiceAttachments.fulfilled, (state, action) => {
        action.payload.data.forEach((invoice) => {
          invoiceDetailedAdapter.updateOne(state, {
            id: invoice.id,
            changes: { attachments: invoice.attachments },
          });
        });
      })
      .addCase(createManualInvoice.pending, (state) => {
        state.status = "pending";
      })
      .addCase(createManualInvoice.fulfilled, (state) => {
        invoiceDetailedAdapter.removeOne(state, "");
        state.status = "succeeded";
      })
      .addCase(createManualInvoice.rejected, (state, action) => {
        state.error = action.error.message;
        state.status = "failed";
      })
      .addCase(copyInvoice.pending, (state) => {
        state.status = "pending";
      })
      .addCase(copyInvoice.fulfilled, (state, action) => {
        invoiceDetailedAdapter.addOne(state, action.payload);
        state.status = "succeeded";
      })
      .addCase(copyInvoice.rejected, (state, action) => {
        state.error = action.error.message;
        state.status = "failed";
      })
      .addCase(updateInvoice.pending, (state) => {
        state.status = "pending";
      })
      .addCase(updateInvoice.fulfilled, (state, action) => {
        if (action.payload.length > 0) {
          const editedPayload = action.payload.find(
            (event) => event.name === "invChg",
          );

          if (editedPayload) {
            const highestVersion = action.payload.reduce(
              (max, event) => Math.max(max, event.version),
              0,
            );
            const { id: invoiceId, payload: invoicePayload } = editedPayload;
            const {
              invoiceDate,
              invoiceValueDate,
              paymentTermDays,
              sellerReferenceIdentifier,
              orderIdentifier,
              agreementIdentifier,
              salesPersonId,
              salesPersonName,
              buyerContactPersonName,
              invoiceRows,
            } = invoicePayload;

            // Update state invoice rows with the new ones
            let changedInvoiceRows: InvoiceRow[] = [];

            if (!!invoiceRows) {
              const getCustomerOrganizationIdByRowId = (rowId: string) => {
                // NOTE: This is a workaround to get the customerOrganizationId and keep state consistent
                // TODO: API should return the customerOrganizationId in the invoiceRows data?
                const argRows = action.meta.arg.invoiceRows;
                const row = argRows.find((row) => row.id === rowId);
                return row?.customerOrganizationId;
              };
              invoiceRows.forEach((row: any) => {
                const customerOrganizationId = getCustomerOrganizationIdByRowId(
                  row.id,
                );
                // Format API data to match the InvoiceRow type
                changedInvoiceRows.push({
                  id: row.id,
                  eezyCostCenterId: row.eezyCostCenter.id,
                  salesAccountCode: row.salesAccount.code,
                  quantityAmount: row.quantityAmount,
                  quantityUnit: row.quantityUnit,
                  unitPriceAmount: row.unitPriceAmount,
                  ...(customerOrganizationId && {
                    customerOrganizationId,
                  }),
                  ...(row.customerCostCenter?.id && {
                    customerCostCenterId: row.customerCostCenter?.id,
                  }),
                  ...(row.eezyBuyerCostCenter?.id && {
                    eezyBuyerCostCenterId: row.eezyBuyerCostCenter?.id,
                  }),
                  discountPercent: row.discountPercent,
                  vatRatePercent: row.vatRatePercent,
                  vatCode: row.vatCode,
                  vatExcludedAmount: row.vatExcludedAmount,
                  vatAmount: row.vatAmount,
                  amount: row.amount,
                  articleName: row.articleName,
                  articleCategory: row.articleCategory,
                  freeText: row.freeText,
                  editable: row.editable,
                  version: row.version,
                });
              });
            }

            let changes: any = {
              version: highestVersion,
              invoiceStatus: INVOICE_EDITED,
              invoiceDate,
              invoiceValueDate,
              paymentTermDays,
              sellerReferenceIdentifier,
              orderIdentifier,
              agreementIdentifier,
              salesPersonId,
              salesPersonName,
              buyerContactPersonName,
            };
            if (changedInvoiceRows.length > 0) {
              changes = { ...changes, invoiceRows: changedInvoiceRows };
            }
            invoiceDetailedAdapter.updateOne(state, {
              id: invoiceId,
              changes,
            });
          }
        }
        state.status = "succeeded";
      })
      .addCase(updateInvoice.rejected, (state, action) => {
        state.error = action.error.message;
        state.status = "failed";
      })
      .addCase(changeInvoiceWave.pending, (state) => {
        state.status = "pending";
      })
      .addCase(changeInvoiceWave.fulfilled, (state, action) => {
        action.payload.forEach(({ id, payload }) => {
          invoiceDetailedAdapter.updateOne(state, {
            id,
            changes: { invoiceWave: payload.invoiceWave },
          });
        });
        state.status = "succeeded";
      })
      .addCase(changeInvoiceWave.rejected, (state, action) => {
        state.error = action.error.message;
        state.status = "failed";
      })
      .addCase(sendInvoices.pending, (state) => {
        state.status = "pending";
      })
      .addCase(sendInvoices.fulfilled, (state, action) => {
        const changes: any = action.payload.map(
          (updatedEvent) =>
            updatedEvent && {
              id: updatedEvent.id,
              changes: {
                ...(updatedEvent.payload?.invoiceDueDate && {
                  invoiceDueDate: updatedEvent.payload.invoiceDueDate,
                }),
                invoiceStatus: updatedEvent.payload.invoiceStatus,
              },
            },
        );
        invoiceDetailedAdapter.updateMany(state, changes);
        state.status = "succeeded";
      })
      .addCase(sendInvoices.rejected, (state, action) => {
        state.error = action.error.message;
        state.status = "failed";
      })
      .addCase(uploadInvoiceAttachment.pending, (state, action) => {
        const { filename, size, invoiceId } = action.meta.arg;
        const item = localStorage.getItem(LOCAL_STORAGE_KEY);
        const fromLocalStorage: LocalStorageAttachment[] = !!item
          ? JSON.parse(item)
          : [];

        const newAttachment = {
          id: filename,
          uploadStarted: Date.now(),
          path: "invoices",
          pathId: invoiceId,
          filename,
          status: "pending",
          size,
        };
        const updated = [...fromLocalStorage, newAttachment];
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(updated));
      })
      .addCase(uploadInvoiceAttachment.fulfilled, (state, action) => {
        const { filename } = action.meta.arg;
        const item = localStorage.getItem(LOCAL_STORAGE_KEY);
        const fromLocalStorage: LocalStorageAttachment[] = !!item
          ? JSON.parse(item)
          : [];

        const updated = fromLocalStorage.map((attachment) =>
          attachment.id === filename
            ? {
                ...attachment,
                status: "scanning",
                id: action.payload,
                filename: action.payload,
              }
            : attachment,
        );
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(updated));
      })
      .addCase(uploadInvoiceAttachment.rejected, (state, action) => {
        const { filename } = action.meta.arg;
        const item = localStorage.getItem(LOCAL_STORAGE_KEY);
        const fromLocalStorage: LocalStorageAttachment[] = !!item
          ? JSON.parse(item)
          : [];

        const updated = fromLocalStorage.map((attachment) =>
          attachment.filename === filename
            ? { ...attachment, status: "failed" }
            : attachment,
        );

        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(updated));
      })
      .addCase(deleteAttachment.pending, (state, action) => {
        const { invoiceId: id, filename } = action.meta.arg;
        // @ts-ignore
        const invoiceEntity = state.entities[id] as InvoiceEdit;
        const invoiceAttachments = invoiceEntity.attachments!!;
        invoiceDetailedAdapter.updateOne(state, {
          id,
          changes: {
            attachments: [
              ...invoiceAttachments.map((attachment) =>
                attachment.filename === filename
                  ? {
                      ...attachment,
                      status: "pending",
                    }
                  : attachment,
              ),
            ],
          },
        });
      })
      .addCase(deleteAttachment.fulfilled, (state, action) => {
        const { invoiceId: id, filename } = action.meta.arg;
        // @ts-ignore
        const invoiceEntity = state.entities[id] as InvoiceEdit;
        const invoiceAttachments = invoiceEntity.attachments!!;
        invoiceDetailedAdapter.updateOne(state, {
          id,
          changes: {
            attachments: [
              ...invoiceAttachments.filter(
                (attachment) => attachment.filename !== filename,
              ),
            ],
          },
        });
      })
      .addCase(deleteAttachment.rejected, (state, action) => {
        const { invoiceId: id, filename } = action.meta.arg;
        // @ts-ignore
        const invoiceEntity = state.entities[id] as InvoiceEdit;
        const invoiceAttachments = invoiceEntity.attachments!!;
        invoiceDetailedAdapter.updateOne(state, {
          id,
          changes: {
            attachments: [
              ...invoiceAttachments.map((attachment) =>
                attachment.filename === filename
                  ? {
                      ...attachment,
                      status: "failed",
                    }
                  : attachment,
              ),
            ],
          },
        });
      })
      .addCase(fetchAttachmentDownloadUrl.pending, (state, action) => {
        const { invoiceId: id, filename } = action.meta.arg;
        // @ts-ignore
        const invoiceEntity = state.entities[id] as InvoiceEdit;
        const invoiceAttachments = invoiceEntity.attachments!!;
        invoiceDetailedAdapter.updateOne(state, {
          id,
          changes: {
            attachments: [
              ...invoiceAttachments.map((attachment) =>
                attachment.filename === filename
                  ? {
                      ...attachment,
                      status: "pending",
                    }
                  : attachment,
              ),
            ],
          },
        });

        state.status = "pending";
      })
      .addCase(fetchAttachmentDownloadUrl.fulfilled, (state, action) => {
        const { invoiceId: id, filename } = action.meta.arg;
        // @ts-ignore
        const invoiceEntity = state.entities[id] as InvoiceEdit;
        const invoiceAttachments = invoiceEntity.attachments!!;
        invoiceDetailedAdapter.updateOne(state, {
          id,
          changes: {
            attachments: [
              ...invoiceAttachments.map((attachment) =>
                attachment.filename === filename
                  ? {
                      ...attachment,
                      status: "succeeded",
                    }
                  : attachment,
              ),
            ],
          },
        });
        state.status = "succeeded";
      })
      .addCase(fetchAttachmentDownloadUrl.rejected, (state, action) => {
        state.error = action.error.message;
        state.status = "failed";
      })
      .addCase(deleteInvoice.fulfilled, (state) => {
        // Clear the state
        invoiceDetailedAdapter.setAll(state, []);
      })
      .addCase(deleteInvoicesBundle.fulfilled, (state) => {
        // Clear the state
        invoiceDetailedAdapter.setAll(state, []);
      })
      .addCase(refreshInvoiceData.pending, (state) => {
        state.status = "pending";
      })
      .addCase(refreshInvoiceData.fulfilled, (state, action) => {
        const invoice = action.payload.reduce((acc, event) => {
          if (event.payload) {
            return {
              ...acc,
              ...event.payload,
            };
          }
          return acc;
        }, {} as InvoiceEdit);
        invoiceDetailedAdapter.updateOne(state, {
          id: action.meta.arg.toString(),
          changes: invoice,
        });
        state.status = "succeeded";
      })
      .addCase(refreshInvoiceData.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(getAttachmentsFromLocalStorage, (state, action) => {
        // Get attachments from local storage to redux store
        const { invoiceId, attachments: fromStorage } = action.payload;
        // @ts-ignore
        const invoiceEntity = state.entities[invoiceId] as InvoiceEdit;
        const invoiceAttachments = invoiceEntity.attachments || [];

        // Don't add attachments that are already in the state or over five minutes old
        const newAttachments: InvoiceAttachment[] = fromStorage.filter(
          (attachment) =>
            Date.now() - attachment.uploadStarted < 300000 &&
            !invoiceAttachments.find((a) => a.filename === attachment.id),
        );
        invoiceDetailedAdapter.updateOne(state, {
          id: invoiceId,
          changes: {
            attachments: [...invoiceAttachments, ...newAttachments],
          },
        });
      })
      .addCase(updateAttachmentsInLocalStorage, (state, action) => {
        // Refresh attachments in local storage
        const { invoiceId, localStorageData } = action.payload;
        // @ts-ignore
        const invoiceEntity = state.entities[invoiceId] as InvoiceEdit;
        const invoiceAttachments = invoiceEntity.attachments || [];

        const allAttachments =
          localStorageData &&
          localStorageData.filter(
            (attachment) => attachment.path === "invoices",
          );

        const otherInvoiceAttachments = allAttachments.filter(
          (attachment) => attachment.pathId !== invoiceId,
        );

        // Remove items that are over five minutes old (probably failed in virus scan)
        // and items that are finished uploading to server.
        const updatedAttachments = allAttachments.filter(
          (attachment) =>
            attachment.pathId === invoiceId &&
            Date.now() - attachment.uploadStarted < 300000 &&
            !invoiceAttachments.find(
              (invoiceAttachment) =>
                invoiceAttachment.filename === attachment.filename &&
                invoiceAttachment.status !== "scanning",
            ),
        );
        localStorage.setItem(
          LOCAL_STORAGE_KEY,
          JSON.stringify([...otherInvoiceAttachments, ...updatedAttachments]),
        );
      })
      .addCase(convertToCreditInvoice.pending, (state) => {
        state.status = "pending";
      })
      .addCase(convertToCreditInvoice.fulfilled, (state, action) => {
        state.status = "succeeded";
      })
      .addCase(convertToCreditInvoice.rejected, (state) => {
        state.status = "failed";
      });
  },
});

export const { selectAll, selectById, selectIds } =
  invoiceDetailedAdapter.getSelectors(
    (state: RootState) => state.invoicesDetailed,
  );

export default invoiceDetailedSlice.reducer;
